import React from 'react';
import { FiSettings } from 'react-icons/fi';
import {
  FaCalendar,
  FaEuroSign,
  FaHardDrive,
  FaHandshake,
  FaListCheck,
  FaMoneyBillTransfer,
  FaStar,
} from 'react-icons/fa6';
import { BiSolidPhotoAlbum } from 'react-icons/bi';
import { RiOrganizationChart } from 'react-icons/ri';
import { MdOutlineQuiz } from 'react-icons/md';
import { FaBalanceScale, FaMoneyBill, FaChartLine, FaStore, FaUsers, FaCog, FaLink } from 'react-icons/fa';
import { HiOutlineChatBubbleLeftEllipsis, HiOutlineHome, HiUserGroup } from 'react-icons/hi2';
import { useSelector } from 'react-redux';
import { TbScoreboard } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import Navigation from './Navigation';
import NavGroup from './NavGroup';
import { useBetaFeature } from '../../../../../hooks/useBetaFeature';
import { RootState } from '../../../../../redux/reducers';
import { SubscriptionType } from '../../../../../types/misc';
import useActiveSubscription from '../../../../../hooks/community/useActiveSubscription';
import useSelectedCommunity from '../../../../../hooks/community/useSelectedCommunity';
import useSelectedBusiness from '../../../../../hooks/business/useSelectedBusiness';
import { BusinessRole } from '../../../../../types/business';
import { Feature, getBusinessSettingsFeatures } from '../../../../../utils/FeatureUtils';

export default function DropdownPanel(): JSX.Element {
  const { t } = useTranslation();
  const community = useSelectedCommunity();
  const business = useSelectedBusiness();
  const activeSubscription = useActiveSubscription();
  const hasSubscription = activeSubscription !== SubscriptionType.FREE;
  const role = useSelector((state: RootState) => state.application.role);

  const isInBusinessMode = process.env.REACT_APP_APPLICATION_VERSION === 'BUSINESS';
  const isPaidByBusiness = community?.paidByBusiness || false;
  const businessFeatures = getBusinessSettingsFeatures(business);
  const canManageSponsors =
    isInBusinessMode &&
    businessFeatures[Feature.SPONSORS] === true &&
    (business?.role === BusinessRole.OWNER || business?.role === BusinessRole.MANAGER);

  const hasFinancialAccess = useBetaFeature('FINANCIAL_ADMINISTRATION');
  const hasSalesAccess = useBetaFeature('SALES');
  const hasFilesAccess = useBetaFeature('FILES');

  const financialItems = [
    {
      icon: <FaChartLine className="h-5 w-5 flex-shrink-0" />,
      name: t('page.financial.overview.title'),
      path: '/financial/overview',
    },
    {
      icon: <FaMoneyBillTransfer className="h-5 w-5 flex-shrink-0" />,
      name: t('page.financial.transactions.title'),
      condition: community?.features?.FINANCIAL_ADMINISTRATION,
      path: '/financial/transactions',
    },
    {
      icon: <FaUsers className="h-5 w-5 flex-shrink-0" />,
      name: t('page.financial.members.title'),
      condition: community?.features?.FINANCIAL_ADMINISTRATION,
      path: '/financial/members',
    },
    {
      icon: <FaBalanceScale className="h-5 w-5 flex-shrink-0" />,
      name: t('page.financial.reports.title'),
      path: '/financial/reports',
    },
    {
      icon: <FaCog className="h-5 w-5 flex-shrink-0" />,
      name: t('page.financial.settings.title'),
      condition: community?.features?.FINANCIAL_ADMINISTRATION,
      path: '/financial/settings',
    },
  ].filter((r) => r.condition === undefined || r.condition);

  const featureItems = [
    {
      icon: <FaCalendar className="h-5 w-5 flex-shrink-0" />,
      name: t('page.event.regular.title'),
      condition: community?.features?.EVENTS || false,
      path: '/events',
    },
    {
      icon: <TbScoreboard className="h-5 w-5 flex-shrink-0" />,
      name: t('page.lists.score.title'),
      condition: community?.features?.STATS_AND_SCORES || false,
      path: '/lists/score',
    },
    {
      icon: <FaListCheck className="h-5 w-5 flex-shrink-0" />,
      name: t('page.lists.task.title'),
      condition: community?.features?.TASKS || false,
      path: '/lists/task',
    },
    {
      icon: <BiSolidPhotoAlbum className="h-5 w-5 flex-shrink-0" />,
      name: t('page.albums.title'),
      condition: community?.features?.PHOTO_ALBUMS || false,
      path: '/albums',
    },
    {
      icon: <MdOutlineQuiz className="h-5 w-5 flex-shrink-0" />,
      name: t('page.forms.title'),
      condition: community?.features?.FORMS || false,
      path: '/forms',
    },
    {
      icon: <FaStore className="h-5 w-5 flex-shrink-0" />,
      name: t('page.financial.salesCollect.title'),
      beta: true,
      condition: (hasSalesAccess && community?.features?.SALES) || false,
      path: '/financial/sales-collect',
    },
    {
      icon: <FaHardDrive className="h-5 w-5 flex-shrink-0" />,
      name: t('page.files.title'),
      beta: true,
      condition: (hasFilesAccess && community?.features?.FILES) || false,
      path: '/files',
    },
  ].filter((r) => r.condition === undefined || r.condition);

  const managementItems = [
    {
      icon: <FaEuroSign className="h-5 w-5 flex-shrink-0" />,
      name: t('page.subscriptions.title'),
      condition: !hasSubscription && !isInBusinessMode && !isPaidByBusiness,
      path: '/settings/subscriptions',
    },
    {
      icon: <FaStar className="h-5 w-5 flex-shrink-0" />,
      name: t('page.subscriptions.manageSubscriptionLink'),
      condition: hasSubscription && !isInBusinessMode && !isPaidByBusiness,
      path: '/subscription/manage',
    },
    {
      icon: <FaStar className="h-5 w-5 flex-shrink-0" />,
      name: t('page.subscriptions.manageSubscriptionLink'),
      condition: isInBusinessMode,
      path: '/business/subscription',
    },
    {
      icon: <FaMoneyBill className="h-5 w-5 flex-shrink-0" />,
      name: t('page.affiliate.title'),
      path: '/affiliate',
    },
    {
      icon: <FaLink className="h-5 w-5 flex-shrink-0" />,
      name: t('page.crm.title'),
      condition: isInBusinessMode,
      path: '/integrations',
    },
    {
      icon: <FaHandshake className="h-5 w-5 flex-shrink-0" />,
      name: t('page.sponsors.title'),
      condition: canManageSponsors,
      path: '/sponsors',
    },
    {
      icon: <FiSettings className="h-5 w-5 flex-shrink-0" />,
      name: t('page.settings.general.title'),
      id: 'settings',
      path: '/settings',
    },
  ].filter((r) => r.condition === undefined || r.condition);

  return (
    <div className="flex w-full flex-col gap-1">
      <NavGroup label={t('nav.general', { defaultValue: 'General' })}>
        {!isInBusinessMode && (
          <Navigation
            id="overview"
            icon={<HiOutlineHome className="h-5 w-5" />}
            name={t('general.overview')}
            path="/overview"
          />
        )}
        <Navigation
          id="contacts"
          icon={<HiUserGroup className="h-5 w-5" />}
          name={t('page.dashboard.clientList.title')}
          path="/contacts"
        />
        {isInBusinessMode && (
          <Navigation
            id="my-communities"
            icon={<RiOrganizationChart className="h-5 w-5" />}
            name={t('page.communities.title')}
            path="/my-communities"
          />
        )}
        <Navigation
          id="communication"
          icon={<HiOutlineChatBubbleLeftEllipsis className="h-5 w-5" />}
          name={t('general.communication')}
          path="/communication"
        />
      </NavGroup>

      {featureItems.length > 0 && (
        <NavGroup label={t('nav.features', { defaultValue: 'Features' })}>
          {featureItems.map((r) => (
            <Navigation {...r} key={r.path} />
          ))}
        </NavGroup>
      )}

      {hasFinancialAccess && financialItems.length > 0 && (
        <NavGroup label={t('nav.finances', { defaultValue: 'Finances' })}>
          {financialItems.map((r) => (
            <Navigation {...r} key={r.path} />
          ))}
        </NavGroup>
      )}

      {managementItems.length > 0 && (
        <NavGroup label={t('nav.management', { defaultValue: 'Management' })}>
          {managementItems.map((r) => (
            <Navigation {...r} key={r.path} />
          ))}
        </NavGroup>
      )}

      {(process.env.NODE_ENV === 'development' || (role && role === 'ADMIN')) && (
        <Navigation
          id="send-mas"
          icon={<FaStar className="h-5 w-5" />}
          name="send mas"
          path="/send-mas"
        />
      )}
    </div>
  );
}
